/**
 * Category-grid model (R12 field-fix F2 — "categories were read aloud, not shown").
 *
 * Field evidence (dev-73, 2026-07-08 turns 4–7): "show me the categories" → Gigi narrated the
 * full 12-category list; the showcase never changed. There was no category-grid render path.
 *
 * R12 gives the showcase a category grid. The server (concierge R29) pushes the 12 category
 * slugs + display names over the render path. This module parses TOLERANTLY so the client works
 * whichever shape the server ships:
 *
 *   a) R29 FROZEN (voice-capture-contract.md §9 v4.0):
 *      `{ type:'ui.categories', payload:{ categories:[{slug, name, count}] } }` — no image; `count`
 *      is the per-category product count (ignored by the grid; available if we later surface it).
 *   b) a dedicated `ui.show_categories { categories: [{slug, name, image?}] }` message, or
 *   c) a mode of the existing `ui.show_products` render — `{ mode: 'categories', categories: [...] }`
 *      or a cards payload whose items carry a `slug` + `name` but no product SKU.
 *
 * `parseCategoryGrid` accepts any of these (and unwraps a nested `payload.categories`) and
 * normalises to `CategoryGridItem[]`. Unknown / malformed payloads return null (the caller then
 * leaves the existing browse surface untouched).
 *
 * This module is pure (no React / no DOM) so parsing + fallbacks are unit-testable.
 */

import type { IconName } from '../components/Icon';

/** One tile in the category grid: a DB category slug + its display name + optional hero image. */
export type CategoryGridItem = {
  /** Exact DB `products.category` slug — the server does an exact match on browse. */
  slug: string;
  /** Human display name ("Wall Mirrors"). */
  name: string;
  /** Representative image URL, if the server supplied one (else a type-only tile renders). */
  image?: string;
};

/**
 * The 13 real DB category slugs (db/seeds/godgift-catalogue.json) → display name + a line icon.
 * Used as the fallback vocabulary when the server payload omits a display name, and to order/curate
 * a server payload into a stable, exhibition-grade sequence. The 13 mirror the catalogue exactly:
 *   animals_insects | furniture | hurricanes_candles | leaf | planters | sculpture_object |
 *   table_clock | table_mirror_frame | trays_bowls | vases | wall_art | wall_clock | wall_mirror
 *
 * R29 X5 — `hurricanes_candles` ("Hurricanes & Candles") was the 13th DB category missing from this
 * static kiosk list (R45 dynamic-vocab + taxonomy §A canon confirm the DB truth is 13; the copy has
 * always read "13 categories"). Added here so the pre-connect teaser + the fallback grid match the DB.
 */
export const CATEGORY_META: Record<string, { name: string; icon: IconName }> = {
  vases: { name: 'Vases', icon: 'swatchPalette' },
  trays_bowls: { name: 'Trays & Bowls', icon: 'grid' },
  wall_mirror: { name: 'Wall Mirrors', icon: 'image' },
  table_mirror_frame: { name: 'Table Mirrors & Frames', icon: 'image' },
  wall_art: { name: 'Wall Art', icon: 'sparkle' },
  wall_clock: { name: 'Wall Clocks', icon: 'clock' },
  table_clock: { name: 'Table Clocks', icon: 'clock' },
  planters: { name: 'Planters', icon: 'heart' },
  furniture: { name: 'Furniture', icon: 'home' },
  sculpture_object: { name: 'Sculptures', icon: 'star' },
  animals_insects: { name: 'Animals & Insects', icon: 'sparkle' },
  hurricanes_candles: { name: 'Hurricanes & Candles', icon: 'candle' },
  leaf: { name: 'Leaf', icon: 'heart' },
};

/** Stable exhibition-grade display order for the 13 tiles (highest-traffic first). */
export const CATEGORY_ORDER: readonly string[] = [
  'vases',
  'trays_bowls',
  'wall_mirror',
  'wall_art',
  'wall_clock',
  'planters',
  'furniture',
  'sculpture_object',
  'table_mirror_frame',
  'table_clock',
  'animals_insects',
  'hurricanes_candles',
  'leaf',
];

/** Prettify an unknown slug into a display name ("table_mirror_frame" → "Table Mirror Frame"). */
export function slugToName(slug: string): string {
  return slug
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/** Icon for a slug (falls back to a neutral grid glyph for unknown slugs). */
export function iconForCategory(slug: string): IconName {
  return CATEGORY_META[slug]?.icon ?? 'grid';
}

/** Display name for a slug: server-provided name > curated meta > prettified slug. */
export function nameForCategory(slug: string, provided?: string): string {
  const p = provided?.trim();
  if (p) return p;
  return CATEGORY_META[slug]?.name ?? slugToName(slug);
}

type RawItem = {
  slug?: unknown;
  category?: unknown; // some server shapes reuse `category` as the slug key
  name?: unknown;
  label?: unknown;
  image?: unknown;
  image_url?: unknown;
};

/** Extract a single normalised item from a loosely-shaped server entry (null if no usable slug). */
function normaliseItem(raw: RawItem): CategoryGridItem | null {
  const slug =
    typeof raw.slug === 'string' && raw.slug.trim() !== ''
      ? raw.slug.trim()
      : typeof raw.category === 'string' && raw.category.trim() !== ''
        ? raw.category.trim()
        : null;
  if (!slug) return null;
  const providedName =
    typeof raw.name === 'string' ? raw.name : typeof raw.label === 'string' ? raw.label : undefined;
  const image =
    typeof raw.image === 'string' && raw.image.trim() !== ''
      ? raw.image
      : typeof raw.image_url === 'string' && raw.image_url.trim() !== ''
        ? raw.image_url
        : undefined;
  return { slug, name: nameForCategory(slug, providedName), image };
}

/**
 * Order + de-duplicate a normalised list into the exhibition sequence. Known slugs sort by
 * CATEGORY_ORDER; any unknown slugs the server sends are appended in arrival order (never dropped).
 */
export function orderCategories(items: CategoryGridItem[]): CategoryGridItem[] {
  const seen = new Set<string>();
  const deduped: CategoryGridItem[] = [];
  for (const it of items) {
    if (seen.has(it.slug)) continue;
    seen.add(it.slug);
    deduped.push(it);
  }
  const rank = (slug: string) => {
    const i = CATEGORY_ORDER.indexOf(slug);
    return i === -1 ? CATEGORY_ORDER.length : i;
  };
  return deduped
    .map((it, i) => ({ it, i }))
    .sort((a, b) => rank(a.it.slug) - rank(b.it.slug) || a.i - b.i)
    .map(({ it }) => it);
}

/**
 * Parse a loosely-shaped server render payload into an ordered category grid, or null if the
 * payload is not a category grid. Tolerant of the R29 shapes (see module header). Accepts either
 * the payload object itself OR the whole message (it unwraps a nested `payload.categories`).
 */
export function parseCategoryGrid(payload: unknown): CategoryGridItem[] | null {
  if (!payload || typeof payload !== 'object') return null;
  type Shape = { mode?: unknown; categories?: unknown; cards?: unknown; items?: unknown };
  const outer = payload as Shape & { payload?: unknown };
  // R29 frozen shape nests the array under `payload` (`ui.categories`); the mode:'categories'
  // compat shape carries `mode` at the top level. Merge both scopes so either resolves whether the
  // caller passes the whole message or just the payload object.
  const nested = outer.payload && typeof outer.payload === 'object' ? (outer.payload as Shape) : {};
  const p: Shape = {
    mode: outer.mode ?? nested.mode,
    categories: nested.categories ?? outer.categories,
    items: outer.items ?? nested.items,
    cards: outer.cards ?? nested.cards,
  };
  // Explicit categories array (dedicated message OR mode:'categories' on the render path).
  const arr =
    Array.isArray(p.categories) && p.categories.length > 0
      ? p.categories
      : p.mode === 'categories' && Array.isArray(p.items)
        ? p.items
        : p.mode === 'categories' && Array.isArray(p.cards)
          ? p.cards
          : null;
  if (!arr) return null;
  const items = (arr as RawItem[])
    .map(normaliseItem)
    .filter((x): x is CategoryGridItem => x !== null);
  if (items.length === 0) return null;
  return orderCategories(items);
}

/**
 * The default 13-tile grid to render when the visitor asks to see categories but the server hasn't
 * pushed a payload yet (or pushed one the client couldn't parse) — the showcase should still SHOW
 * the categories, never leave the visitor with narration only. Images are absent (type-only tiles).
 */
export function defaultCategoryGrid(): CategoryGridItem[] {
  return CATEGORY_ORDER.map((slug) => ({ slug, name: nameForCategory(slug) }));
}
